import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

/**
 * Member Student Eligibility & Enrollment Proof — Phase 1 (Domain/Data/
 * Security). Source: `docs/incoming/member-student-eligibility-and-
 * enrollment-proof-sdd.md` §6 Domain Model / §7 Data Contract, mapped to
 * DM-037..039 / DATA-046..048.
 *
 * Phase 1 does not wire these into the Registration submit gate
 * (API-ELIG-007 / spec §8's last endpoint) — that is Phase 2. Nothing here
 * changes existing Registration behavior.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const STUDENT_ELIGIBILITY_REQUIREMENT_SCOPE_VALUES = [
  'all_student_members',
  'all_participating_members',
  'team_leader_only',
  'minimum_verified_members',
] as const
export const studentEligibilityRequirementScopeSchema = z.enum(
  STUDENT_ELIGIBILITY_REQUIREMENT_SCOPE_VALUES,
)
export type StudentEligibilityRequirementScope = z.infer<
  typeof studentEligibilityRequirementScopeSchema
>

export const ENROLLMENT_PROOF_TYPE_VALUES = [
  'enrollment_certificate',
  'student_id',
  'other_approved',
] as const
export const enrollmentProofTypeSchema = z.enum(ENROLLMENT_PROOF_TYPE_VALUES)
export type EnrollmentProofType = z.infer<typeof enrollmentProofTypeSchema>

/**
 * DATA-ELIG-004 file-type allowlist for enrollment proof uploads —
 * deliberately a narrower subset of `ALLOWED_FILE_EXTENSIONS` (submission
 * uploads' full list includes zip/video/office formats that make no sense
 * for a document meant to prove current enrollment).
 */
export const ENROLLMENT_PROOF_ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'] as const

export const ENROLLMENT_PROOF_STATUS_VALUES = [
  'uploaded',
  'pending',
  'verified',
  'rejected',
  'superseded',
  'expired',
] as const
export const enrollmentProofStatusSchema = z.enum(ENROLLMENT_PROOF_STATUS_VALUES)
export type EnrollmentProofStatus = z.infer<typeof enrollmentProofStatusSchema>

export const MEMBER_ELIGIBILITY_STATUS_VALUES = [
  'missing',
  'pending',
  'verified',
  'rejected',
  'expired',
] as const
export const memberEligibilityStatusSchema = z.enum(MEMBER_ELIGIBILITY_STATUS_VALUES)
export type MemberEligibilityStatus = z.infer<typeof memberEligibilityStatusSchema>

export const ELIGIBILITY_REVIEW_DECISION_VALUES = ['verified', 'rejected'] as const
export const eligibilityReviewDecisionSchema = z.enum(ELIGIBILITY_REVIEW_DECISION_VALUES)
export type EligibilityReviewDecision = z.infer<typeof eligibilityReviewDecisionSchema>

// ---------------------------------------------------------------------------
// DM-037 — CompetitionEligibilityPolicy
// ---------------------------------------------------------------------------

export const competitionEligibilityPolicySchema = z.object({
  competitionId: publicIdSchema,
  studentEligibilityRequired: z.boolean(),
  requirementScope: studentEligibilityRequirementScopeSchema.nullable(),
  minimumVerifiedMembers: z.int().min(0).nullable(),
  acceptedProofTypes: z.array(enrollmentProofTypeSchema),
  reviewRequired: z.boolean(),
  /**
   * GAP-ELIG-005 (SDD §22): the spec's own suggested baseline default is
   * `false`. The product owner explicitly chose `true` as this repo's
   * default instead — see `docs/sdd/09-delivery-change-control.md` v3.83
   * and `docs/delivery-log/2026-08-26-v3.83-*.md` for the recorded
   * decision. Phase 1 never evaluates this flag (no registration gate is
   * wired yet — that is Phase 2/API-ELIG-007), so the choice has no
   * observable effect until then.
   */
  allowSubmitWhilePending: z.boolean(),
  proofValidityDays: z.int().min(1).nullable(),
  updatedAt: utcTimestampSchema,
})
export type CompetitionEligibilityPolicy = z.infer<typeof competitionEligibilityPolicySchema>

export const eligibilityPolicyDtoSchema = competitionEligibilityPolicySchema
export type EligibilityPolicyDto = z.infer<typeof eligibilityPolicyDtoSchema>

/**
 * API-ELIG-001: "Student-safe DTO 不得包含 administrative-only fields." The
 * policy today has no field that is unsafe for a participant to see (every
 * field describes a requirement the participant must satisfy) — this is a
 * distinct schema anyway so a future administrative-only field (e.g. an
 * internal review-queue SLA target) has somewhere to go without widening
 * what a participant's `GET .../eligibility-policy` response carries.
 */
export const eligibilityPolicyPublicDtoSchema = competitionEligibilityPolicySchema.omit({
  updatedAt: true,
})
export type EligibilityPolicyPublicDto = z.infer<typeof eligibilityPolicyPublicDtoSchema>

export const updateEligibilityPolicyRequestSchema = z.strictObject({
  studentEligibilityRequired: z.boolean().optional(),
  requirementScope: studentEligibilityRequirementScopeSchema.nullable().optional(),
  minimumVerifiedMembers: z.int().min(0).nullable().optional(),
  acceptedProofTypes: z.array(enrollmentProofTypeSchema).optional(),
  reviewRequired: z.boolean().optional(),
  allowSubmitWhilePending: z.boolean().optional(),
  proofValidityDays: z.int().min(1).nullable().optional(),
})
export type UpdateEligibilityPolicyRequest = z.infer<typeof updateEligibilityPolicyRequestSchema>

// ---------------------------------------------------------------------------
// DM-038 — EnrollmentProof (versioned child entity; owns the private file)
// ---------------------------------------------------------------------------

export const enrollmentProofSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  userId: publicIdSchema,
  type: enrollmentProofTypeSchema,
  r2Key: z.string().min(1),
  originalFilename: z.string().min(1),
  displayFilename: z.string().min(1),
  contentType: z.string().min(1),
  extension: z.string().min(1).max(16),
  sizeBytes: z.int().min(0),
  etag: z.string().nullable(),
  status: enrollmentProofStatusSchema,
  issuedAt: utcTimestampSchema.nullable(),
  expiresAt: utcTimestampSchema.nullable(),
  uploadExpiresAt: utcTimestampSchema.nullable(),
  uploadedAt: utcTimestampSchema,
  reviewedAt: utcTimestampSchema.nullable(),
  reviewedByUserId: publicIdSchema.nullable(),
  reviewReason: z.string().nullable(),
  supersededByProofId: publicIdSchema.nullable(),
  /**
   * PRIV-011/AC-243 (v3.86 Phase 4 retention). Non-null once the automated
   * 365-days-after-competition-end retention sweep has deleted the R2
   * object — `status`/`reviewedAt`/`reviewedByUserId`/`reviewReason` (the
   * verification *outcome*) are preserved unchanged; only the file itself is
   * gone. Downstream code must treat this as "never call R2 for this proof
   * again" — see `routes/eligibility.ts`'s download/stream handlers.
   */
  filePurgedAt: utcTimestampSchema.nullable(),
})
export type EnrollmentProof = z.infer<typeof enrollmentProofSchema>

/**
 * Owner's own view of their proof. Never carries `r2Key`/`filePurgedAt` —
 * internal storage/retention bookkeeping, not something a student needs to
 * see (a purged proof simply 404s on download, same as any other
 * not-found).
 */
export const enrollmentProofOwnDtoSchema = enrollmentProofSchema.omit({
  competitionId: true,
  r2Key: true,
  uploadExpiresAt: true,
  filePurgedAt: true,
})
export type EnrollmentProofOwnDto = z.infer<typeof enrollmentProofOwnDtoSchema>

/**
 * SEC-ELIG-004/PRIV-ELIG-002 reviewer/review-queue projection: data
 * minimization — no `studentNumber` is even present on this type (that
 * lives on `MemberEligibility`, and the review-queue DTO below omits it),
 * and this never carries `r2Key`, a signed URL, or the file's raw bytes.
 *
 * v1.1 IA (Eligibility Data page, SDD §4.3.4/§12.9) additively restores
 * `filePurgedAt`: PRIV-011 retention already purges the raw file body 365
 * days after competition end while keeping the verification outcome intact,
 * and the Operations/Data surfaces need to tell "檔案已依保存政策清除" apart
 * from "missing" (§12.9 — a purged proof MUST NOT be reported as
 * `missing`). `filePurgedAt` never enables a new download path — the
 * proof/:proofId/download route above still independently 404s once it is
 * non-null — so restoring it here is read-only additive information, not a
 * privacy regression. The Admin Review Workbench
 * (`competition-people-eligibility-section.tsx`) does not read this field
 * and is unaffected by its presence.
 */
export const enrollmentProofReviewDtoSchema = enrollmentProofSchema.omit({
  r2Key: true,
  uploadExpiresAt: true,
})
export type EnrollmentProofReviewDto = z.infer<typeof enrollmentProofReviewDtoSchema>

// ---------------------------------------------------------------------------
// DM-039 — MemberEligibility
// ---------------------------------------------------------------------------

export const memberEligibilitySchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  userId: publicIdSchema,
  schoolName: z.string().trim().max(200).nullable(),
  departmentName: z.string().trim().max(200).nullable(),
  /** GAP-ELIG-004 (SDD §22): optional/nullable — never enforced as required in Phase 1. */
  studentNumber: z.string().trim().max(64).nullable(),
  status: memberEligibilityStatusSchema,
  currentProofId: publicIdSchema.nullable(),
  verifiedAt: utcTimestampSchema.nullable(),
  verifiedByUserId: publicIdSchema.nullable(),
  rejectionReason: z.string().nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type MemberEligibility = z.infer<typeof memberEligibilitySchema>

/**
 * API-ELIG-003 own-eligibility DTO. Excludes `verifiedByUserId` (a reviewer
 * identity — not something the spec's §5.3 "在學證明" page or §5.4's data-
 * minimization intent calls for exposing to the person being reviewed) but
 * keeps `rejectionReason` (spec explicitly requires the student see it) and
 * `currentProof`, attached separately below since it's a join, not a column.
 */
export const memberEligibilityOwnDtoSchema = memberEligibilitySchema
  .omit({ verifiedByUserId: true })
  .extend({ currentProof: enrollmentProofOwnDtoSchema.nullable() })
export type MemberEligibilityOwnDto = z.infer<typeof memberEligibilityOwnDtoSchema>

/**
 * API-ELIG-005/006 reviewer-facing DTO — the review queue row and the
 * response of a review decision. Carries `verifiedByUserId` (reviewer
 * accountability) and the member's own declared fields, but never
 * `studentNumber` (PRIV-ELIG-002 minimum-necessary-fields: a reviewer only
 * needs to confirm current enrollment, not collect a student id).
 */
export const memberEligibilityReviewDtoSchema = memberEligibilitySchema
  .omit({ studentNumber: true })
  .extend({
    displayName: z.string(),
    email: z.string(),
    currentProof: enrollmentProofReviewDtoSchema.nullable(),
    /**
     * v3.85 (Phase 3): the member's current team, for the Admin Review
     * Workbench's "隊伍" column (spec §5.4) — `null` when the member has no
     * team yet, same as `TeamEligibilitySummaryDto.teamId`.
     */
    teamId: publicIdSchema.nullable(),
    teamName: z.string().nullable(),
    /**
     * v1.1 IA (Eligibility Data page, SDD §4.3.4/§12.7) additive field:
     * every prior version of this member's proof, oldest first, walked via
     * `supersededByProofId`/`superseded` lineage — never a second review
     * source, just the historical versions this same
     * `GET .../eligibility/reviews` row's current proof superseded. Empty
     * array (not `null`) when the current proof has never been re-uploaded.
     * The Admin Review Workbench does not read this field.
     */
    proofHistory: z.array(enrollmentProofReviewDtoSchema),
  })
export type MemberEligibilityReviewDto = z.infer<typeof memberEligibilityReviewDtoSchema>

export const eligibilityReviewQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  status: memberEligibilityStatusSchema.optional(),
  teamId: publicIdSchema.optional(),
  search: z.string().trim().max(200).optional(),
  /**
   * v1.1 IA (Eligibility Data page): opt-in extra query per row to populate
   * `proofHistory`. Defaults to `false` so the existing Admin Review
   * Workbench call — which never reads `proofHistory` — does not pay for an
   * extra per-row query it does not need. `z.coerce.boolean()` is
   * deliberately not used here — any non-empty query string (including the
   * literal text "false") coerces truthy under `Boolean(...)`, which would
   * silently misparse `?includeHistory=false`.
   */
  includeHistory: z
    .enum(['true', 'false'])
    .optional()
    .default('false')
    .transform((value) => value === 'true'),
})
export type EligibilityReviewQuery = z.infer<typeof eligibilityReviewQuerySchema>

// ---------------------------------------------------------------------------
// API-ELIG-004 — Upload Proof (intent / finalize)
// ---------------------------------------------------------------------------

export const eligibilityProofUploadIntentRequestSchema = z.strictObject({
  proofType: enrollmentProofTypeSchema,
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(255),
  sizeBytes: z.int().min(1),
})
export type EligibilityProofUploadIntentRequest = z.infer<
  typeof eligibilityProofUploadIntentRequestSchema
>

export const eligibilityProofFinalizeRequestSchema = z.strictObject({
  etag: z.string().min(1).nullable().default(null),
  issuedAt: utcTimestampSchema.nullable().default(null),
  schoolName: z.string().trim().min(1).max(200).nullable().optional(),
  departmentName: z.string().trim().max(200).nullable().optional(),
  studentNumber: z.string().trim().max(64).nullable().optional(),
})
export type EligibilityProofFinalizeRequest = z.infer<typeof eligibilityProofFinalizeRequestSchema>

// ---------------------------------------------------------------------------
// API-ELIG-006 — Review Decision
// ---------------------------------------------------------------------------

export const eligibilityReviewDecisionRequestSchema = z.strictObject({
  proofId: publicIdSchema,
  decision: eligibilityReviewDecisionSchema,
  reason: z.string().trim().min(1).max(2000).nullable().default(null),
})
export type EligibilityReviewDecisionRequest = z.infer<
  typeof eligibilityReviewDecisionRequestSchema
>

// ---------------------------------------------------------------------------
// Phase 2 (v3.84) — UX-187/AC-240: Team Eligibility Summary
//
// Deliberately data-minimized for a Team Leader/teammate reading this about
// their *own* team: a status label per member (and `displayName`, already
// visible to any teammate via the existing Team Detail view), never
// `rejectionReason` or `currentProof` for anyone but the caller themselves
// (`isSelf`) — SEC-ELIG-001/002's "no reviewer-only detail leaks to a
// non-reviewer" boundary, extended to this new read.
// ---------------------------------------------------------------------------

export const teamEligibilityMemberSummaryDtoSchema = z.object({
  userId: publicIdSchema,
  displayName: z.string(),
  isSelf: z.boolean(),
  isTeamLeader: z.boolean(),
  required: z.boolean(),
  status: memberEligibilityStatusSchema,
  satisfied: z.boolean(),
  /** Only ever populated when `isSelf` — never a teammate's. */
  rejectionReason: z.string().nullable(),
})
export type TeamEligibilityMemberSummaryDto = z.infer<typeof teamEligibilityMemberSummaryDtoSchema>

export const teamEligibilitySummaryDtoSchema = z.object({
  competitionId: publicIdSchema,
  teamId: publicIdSchema.nullable(),
  required: z.boolean(),
  requiredCount: z.int().min(0),
  satisfiedCount: z.int().min(0),
  /** BR-158 (v4.37): required members with any submitted proof (pending/verified/rejected/expired), regardless of review outcome. */
  submittedCount: z.int().min(0),
  /** BR-158 (v4.37): required members who are actually `verified` — the only status meaning verification is complete. */
  approvedCount: z.int().min(0),
  minimumRemaining: z.int().min(0).nullable(),
  allSatisfied: z.boolean(),
  members: z.array(teamEligibilityMemberSummaryDtoSchema),
})
export type TeamEligibilitySummaryDto = z.infer<typeof teamEligibilitySummaryDtoSchema>
